import { useEffect, useState } from "react";
import { fieldId } from "../utils";
import { useAtomValue } from "jotai";
import { hostDataAtom, useModifyUploadList } from "../../../atoms";
import { tMsg } from "../../ToastNotifications";
import {
  getImageThumbnailFromURL,
  getStillVideoFrameFromURL,
  getThumbFilename,
  uploadFile,
} from "./Utils";
import { Spinner } from "../../Loader";
import { mergeClass } from "../../../utils";
import _ from "lodash";
import { isVideo } from "../../Upload";
import { FieldName } from "../Inputs";
import { format } from "date-fns";

export interface IUploadLocations {
  fileLocation: string | undefined;
  thumbLocation: string | undefined;
}

export const MediaInput = ({
  type,
  thumbUrl,
  name,
  onChange,
  value,
  required,
  hint,
}: {
  type: string;
  thumbUrl?: string | undefined;
  name: string;
  onChange: (value: any) => void;
  value: any;
  hint?: string | React.ReactNode;
  required?: boolean;
}): JSX.Element => {
  const selectorId = `file_select_${name}`;
  const getFieldId = fieldId(name);
  const initValue = {
    fileLocation: _.isString(value) ? value : undefined,
    thumbLocation: thumbUrl,
  };

  const accept = type === "video" ? ".mp4" : ".jpeg, .jpg, .png";

  const [uploadValue, setUploadValue] = useState<IUploadLocations>(initValue);

  const [thumbFile, setThumbFile] = useState<File | null>(null);

  // const [imgUrl, setImgUrl] = useState<string>(_.isString(value) ? value : "");
  const [uploadText, setUploadText] = useState<string>(
    _.isString(value) ? value : ""
  );
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const hostData = useAtomValue(hostDataAtom);

  const [addMainToList, removeMainFromList] = useModifyUploadList(getFieldId);
  const [addThumbToList, removeThumbFromList] = useModifyUploadList(
    `${getFieldId}_thumb`
  );

  // Update upload preview from remote source on element load
  useEffect(() => {
    return () => {
      removeMainFromList();
      removeThumbFromList();
    };
  }, []);

  const fileSelected = async (selectedFile: File | null) => {
    if (selectedFile !== null) {
      const newFilename = `${format(new Date(), "yyyyMMddHHmmss")}_${
        selectedFile.name
      }`;
      const stampedFile = new File([selectedFile], newFilename);
      setThumbFile(null);
      setIsUploading(true);
      startFileUpload(stampedFile);
    } else {
      console.warn("Select a file");
      setThumbFile(null);
    }
  };

  const startFileUpload = async (selectedFile: File | null) => {
    try {
      if (selectedFile === null) {
        tMsg.error("Unable to upload selected file");
        return;
      }
      addMainToList();
      setUploadText(`Uploading - ${selectedFile.name}`);
      uploadFile(
        selectedFile,
        onUploadFinished,
        hostData.awsBucket,
        hostData.awsRegion,
        updateProgress
      );
    } catch (err) {
      tMsg.error("Error uploading file. See console for details");
      console.log(err);
      setIsUploading(false);
      setUploadText("No file selected");
      removeMainFromList();
    }
  };

  const onUploadFinished = (fileLocation: string, file?: File) => {
    // const obj = { target: { value: fileLocation } };
    // onChange(obj);
    const newValue = {
      fileLocation,
      thumbLocation: uploadValue.thumbLocation,
    };
    setUploadValue(newValue);
    onChange(newValue);
    setUploadText(fileLocation);
    removeMainFromList();
    console.log("uploaded file - generating thumb", fileLocation);

    const thumbFilename = getThumbFilename(fileLocation);
    if (file) {
      const thumbDataURL = URL.createObjectURL(file);
      if (isVideo(fileLocation)) {
        getStillVideoFrameFromURL(thumbDataURL, thumbFilename, setThumbFile);
      } else {
        getImageThumbnailFromURL(thumbDataURL, thumbFilename, setThumbFile);
      }
    } else {
      console.warn("mainFile not available for thumb generation.");
      tMsg.warning("Unable to generate thumbnail for preview.");
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (thumbFile !== null) startThumbUpload(thumbFile);
    else if (isUploading) {
      setIsUploading(false);
      console.warn("There may have been an error generating thumbnail.");
    }
  }, [thumbFile]);

  const startThumbUpload = async (selectedFile: File | null) => {
    try {
      if (selectedFile === null) {
        tMsg.error("Unable to upload selected file");
        return;
      }
      addThumbToList();
      uploadFile(
        selectedFile,
        onThumbUploadFinished,
        hostData.awsBucket,
        hostData.awsRegion
      );
    } catch (err) {
      tMsg.error("Error uploading thumbnail. See console for details");
      console.log(err);
      removeThumbFromList();
      setIsUploading(false);
    }
  };

  const updateProgress = (amount: number) => {
    setUploadText(`Uploading - ${amount}%`);
  };

  const onThumbUploadFinished = (thumbLocation: string) => {
    const newValue = {
      fileLocation: uploadValue.fileLocation,
      thumbLocation,
    };
    setIsUploading(false);
    setUploadValue(newValue);
    removeThumbFromList();
    onChange(newValue);
    console.log("uploaded Thumb", thumbLocation);
  };

  return (
    <div className="mb-2 space-y-1">
      <FieldName name={name} hint={hint} required={required} />
      <div className="w-full flex relative">
        <input
          id={getFieldId}
          name={name}
          value={value}
          type="text"
          onChange={(e) => e.preventDefault()}
          required={required}
          className="absolute -z-10 w-full h-full"
        />
        <input
          type="file"
          onChange={(e) =>
            fileSelected(e.target.files ? e.target.files[0] : null)
          }
          className="w-full"
          accept={accept}
          id={selectorId}
          data-has-url={value !== ""}
          data-file-url={value}
          data-is-direct
          hidden
        />
        <div
          id="thumb_container"
          className="w-12 h-12 bg-gray-300 overflow-hidden"
        >
          <img
            src={thumbUrl || "/assets/default-image.jpg"}
            className={mergeClass(`w-full h-full object-cover`, {
              "opacity-40": !value,
            })}
          />
        </div>
        <div className="flex-grow border border-[#ccc] grid content-center px-2.5 text-sm">
          <div className="flex gap-3 overflow-x-auto">
            <span
              className={mergeClass("break-words", {
                "opacity-50": !value,
              })}
            >
              {uploadText}
            </span>
            {isUploading && <Spinner />}
          </div>
        </div>
        <label
          htmlFor={selectorId}
          className={mergeClass(
            "px-5 py-2 grid bg-primaryColor border-primaryColor border text-white font-medium",
            "transition hover:bg-white hover:text-primaryColor hover:border-primaryColor cursor-pointer"
          )}
        >
          {!thumbUrl && !value ? "Select file" : "Change file"}
        </label>
      </div>
    </div>
  );
};
